import React, { useState, useRef, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';

interface ContentWithReadMoreProps {
  content: string;
  feedId: string;
  expandedContent: Set<string>;
  setExpandedContent: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const ContentWithReadMore: React.FC<ContentWithReadMoreProps> = ({
  content,
  feedId,
  expandedContent,
  setExpandedContent
}) => {
  const navigate = useNavigate();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isExpanded = expandedContent.has(feedId);

  // Sanitize HTML content
  const sanitizedContent = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'del', 'strike', 'a', 'ul', 'ol', 'li', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
  });

  useEffect(() => {
    const element = contentRef.current;
    if (element) {
      const lineHeight = parseInt(window.getComputedStyle(element).lineHeight);
      const maxHeight = lineHeight * 3;
      setIsOverflowing(element.scrollHeight > maxHeight);
    }
  }, [content]);

  // Handle link clicks
  const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');

    if (anchor && anchor.href) {
      const url = new URL(anchor.href);
      const currentHost = window.location.hostname;

      // Check if pathname contains a domain (like /hrtalky.in or /google.com)
      const pathDomainMatch = url.pathname.match(/^\/([a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/.*)?)/);

      if (pathDomainMatch) {
        // Extract domain from path and treat as external link
        const extractedDomain = pathDomainMatch[1];
        const externalUrl = `https://${extractedDomain}`;

        console.log('Domain in path detected:', {
          originalHref: anchor.href,
          extractedDomain,
          externalUrl
        });

        e.preventDefault();
        window.open(externalUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      // Check if link is internal (same domain or hrtalky.com)
      const isInternal = url.hostname === currentHost ||
                        url.hostname.includes('hrtalky.com') ||
                        url.hostname === 'localhost';

      console.log('Link clicked:', {
        href: anchor.href,
        hostname: url.hostname,
        currentHost,
        text: anchor.textContent,
        isInternal
      });

      e.preventDefault(); // Always prevent default

      if (isInternal) {
        // For internal links, use React Router
        console.log('Internal link - navigating with React Router to:', url.pathname);
        navigate(url.pathname + url.search + url.hash);
      } else {
        // For external links, open in new tab
        console.log('External link - opening in new tab');
        window.open(anchor.href, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedContent(prev => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.delete(feedId);
      } else {
        newSet.add(feedId);
      }
      return newSet;
    });
  };

  return (
    <Box>
      <Box
        ref={contentRef}
        onClick={handleLinkClick}
        sx={{
          mb: 0,
          lineHeight: 1.6,
          display: '-webkit-box',
          WebkitLineClamp: isExpanded ? 'none' : 3,
          WebkitBoxOrient: 'vertical',
          overflow: isExpanded ? 'visible' : 'hidden',
          '& p': {
            margin: '0.5em 0',
            fontSize: '1rem',
          },
          '& h1': {
            fontSize: '2em',
            fontWeight: 600,
            margin: '0.67em 0',
          },
          '& h2': {
            fontSize: '1.5em',
            fontWeight: 600,
            margin: '0.75em 0',
          },
          '& h3': {
            fontSize: '1.17em',
            fontWeight: 600,
            margin: '0.83em 0',
          },
          '& h4': {
            fontSize: '1em',
            fontWeight: 600,
            margin: '1em 0',
          },
          '& h5': {
            fontSize: '0.83em',
            fontWeight: 600,
            margin: '1.5em 0',
          },
          '& h6': {
            fontSize: '0.67em',
            fontWeight: 600,
            margin: '2em 0',
          },
          '& del, & strike': {
            textDecoration: 'line-through',
          },
          '& blockquote': {
            borderLeft: '3px solid #ddd',
            paddingLeft: '10px',
            color: '#666',
            fontStyle: 'italic',
            margin: '10px 0',
          },
          '& code': {
            backgroundColor: '#f5f5f5',
            padding: '2px 4px',
            borderRadius: '3px',
            fontFamily: 'monospace',
            fontSize: '0.9em',
          },
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
          '& ul, & ol': {
            paddingLeft: '1.5em',
            margin: '0.5em 0',
          },
          '& li': {
            margin: '0.25em 0',
          },
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
      {isOverflowing && (
        <Button
          size="small"
          onClick={toggleExpanded}
          sx={{
            textTransform: 'none',
            p: 0,
            mb: 2,
            minWidth: 'auto',
            color: 'primary.main',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </Button>
      )}
    </Box>
  );
};